import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import PromptManageModal from '../Create-Prompt/PromptManageModal';
import type { CreatePromptDocumentUploadState } from '../Create-Prompt/useCreatePromptDocumentUploads';
import type { CreatePromptVideoUploadState } from '../Create-Prompt/useCreatePromptVideoUploads';
import '../Create-Prompt/Create-Prompt.css';
import '../Prompts/PromptsV2.css';
import { PromptVideoUploadBar } from '../Prompts/PromptVideoUploadBar';
import PromptDescriptionModal from '../PromptDescriptionModal';
import PromptSelectionModal from '../PromptSelectionModal';
import RecordOrUploadModal from '../RecordOrUploadModal';
import { mediaUrl } from '../Prompts/promptsV2Utils';
import {
  refreshRolePromptLibrary,
  cacheRolePromptsFromTexts,
} from '../Prompts/promptsV2RolePrompts';
import { rankedPromptTexts } from '../../utils/parseRankedAiPromptList';
import { loadCachedRolePrompts } from '../../utils/rolePromptCache';
import { isPersistedMediaUrl } from './draftPayload';
import { resolveOnboardingRoleMeta } from './onboardingRolePromptMeta';
import OnboardingPromptCard from './OnboardingPromptCard';
import type { OnboardingDraft, ScreeningPromptItem } from './types';

const MAX_PROMPTS = 6;

type OnboardingPromptSectionProps = {
  promptItems: ScreeningPromptItem[];
  videoFiles: Record<number, File>;
  documentFiles: Record<number, File>;
  videoUrls: Record<number, string>;
  documentUrls: Record<number, string>;
  videoUploads: Record<number, CreatePromptVideoUploadState>;
  documentUploads: Record<number, CreatePromptDocumentUploadState>;
  targetRole: string;
  draft: OnboardingDraft;
  sessionUser: Record<string, unknown> | null;
  emphasized?: boolean;
  variant?: 'default' | 'sidebar';
  onPromptItemsChange: (items: ScreeningPromptItem[]) => void;
  onPromptChange: (index: number, patch: Partial<ScreeningPromptItem>) => void;
  onVideoFile: (index: number, file: File | null) => void;
  onDocumentFile: (index: number, file: File | null) => void;
  onRetryVideoUpload: (index: number) => void;
  onCancelVideoUpload: (index: number) => void;
  onRetryDocumentUpload: (index: number) => void;
  onCancelDocumentUpload: (index: number) => void;
  onPersistPromptDetails?: () => Promise<boolean>;
};

export default function OnboardingPromptSection({
  promptItems,
  videoFiles,
  documentFiles,
  videoUrls,
  documentUrls,
  videoUploads,
  documentUploads,
  targetRole,
  draft,
  sessionUser,
  emphasized = false,
  variant = 'default',
  onPromptItemsChange,
  onPromptChange,
  onVideoFile,
  onDocumentFile,
  onRetryVideoUpload,
  onCancelVideoUpload,
  onRetryDocumentUpload,
  onCancelDocumentUpload,
  onPersistPromptDetails,
}: OnboardingPromptSectionProps) {
  const [expandedPromptIndex, setExpandedPromptIndex] = useState<number | null>(null);
  const [managePromptIndex, setManagePromptIndex] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [recordModalIndex, setRecordModalIndex] = useState<number | null>(null);
  const [modalVideoFiles, setModalVideoFiles] = useState<Record<number, File>>({});
  const [recordErrors, setRecordErrors] = useState<Record<string, string>>({});
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [descriptionIndex, setDescriptionIndex] = useState<number | null>(null);
  const [currentDescription, setCurrentDescription] = useState('');
  const [roleSpecificPool, setRoleSpecificPool] = useState<string[]>([]);
  const [rolePromptsLoading, setRolePromptsLoading] = useState(false);
  const [rolePromptsRefreshing, setRolePromptsRefreshing] = useState(false);

  const roleMeta = useMemo(
    () => resolveOnboardingRoleMeta(targetRole, draft, sessionUser),
    [targetRole, draft, sessionUser]
  );

  const promptArray = useMemo(() => promptItems.map((item) => item.content), [promptItems]);

  const roleSpecificPrompts = useMemo(() => {
    const selected = new Set(promptArray.map((text) => text.trim().toLowerCase()));
    return roleSpecificPool.filter(
      (text) => text.trim() && !selected.has(text.trim().toLowerCase())
    );
  }, [roleSpecificPool, promptArray]);

  const loadRolePromptPool = useCallback(async () => {
    if (!roleMeta.jobTitle?.trim()) {
      setRoleSpecificPool(draft.screeningPrompts || []);
      return;
    }

    setRolePromptsLoading(true);
    try {
      const fromDraft = [...(draft.roleSpecificPrompts || []), ...(draft.screeningPrompts || [])]
        .map((text) => String(text || '').trim())
        .filter(Boolean);
      if (fromDraft.length > 0) {
        cacheRolePromptsFromTexts(roleMeta, fromDraft);
      }
      const cached = rankedPromptTexts(
        loadCachedRolePrompts(roleMeta.jobTitle, roleMeta.jobIndustry, roleMeta.level)
      );
      setRoleSpecificPool(Array.from(new Set([...fromDraft, ...cached])));
    } finally {
      setRolePromptsLoading(false);
    }
  }, [draft.roleSpecificPrompts, draft.screeningPrompts, roleMeta]);

  useEffect(() => {
    void loadRolePromptPool();
  }, [loadRolePromptPool]);

  useEffect(() => {
    if (!pickerOpen) return;
    void loadRolePromptPool();
  }, [pickerOpen, loadRolePromptPool]);

  const handleRefreshRolePrompts = useCallback(async () => {
    if (!roleMeta.jobTitle?.trim()) {
      toast.error('Add a target role before generating role-specific prompts.');
      return;
    }

    setRolePromptsRefreshing(true);
    try {
      const experienceSummary = (draft.workExperience || [])
        .slice(0, 5)
        .map((item) => {
          const head = [item.title, item.company].filter(Boolean).join(' at ');
          const dates = [item.startDate, item.endDate].filter(Boolean).join(' – ');
          return [head, dates, item.description].filter(Boolean).join(' | ');
        })
        .filter(Boolean)
        .join('\n');
      const library = await refreshRolePromptLibrary(roleMeta, {
        overview: draft.overview || '',
        skills: draft.skills || [],
        experienceSummary,
      });
      const texts = rankedPromptTexts(library);
      if (texts.length > 0) {
        setRoleSpecificPool(texts);
        toast.success('Generated a fresh set of role-specific prompts.');
      } else {
        toast.error('Could not generate role-specific prompts. Try again.');
      }
    } catch {
      toast.error('Could not generate role-specific prompts. Try again.');
    } finally {
      setRolePromptsRefreshing(false);
    }
  }, [draft.overview, draft.skills, draft.workExperience, roleMeta]);

  const togglePrompt = (index: number) => {
    setExpandedPromptIndex((prev) => (prev === index ? null : index));
  };

  const openPicker = (index: number) => {
    if (index >= MAX_PROMPTS) {
      toast.info(`You can add up to ${MAX_PROMPTS} screening prompts.`);
      return;
    }
    setEditingIndex(index);
    setPickerOpen(true);
  };

  const handleAddPrompt = () => {
    if (promptItems.length >= MAX_PROMPTS) {
      toast.info(`You can add up to ${MAX_PROMPTS} screening prompts.`);
      return;
    }
    openPicker(promptItems.length);
  };

  const handlePromptSelect = (prompt: string) => {
    const index = editingIndex ?? promptItems.length;
    if (index < promptItems.length) {
      onPromptChange(index, { content: prompt });
    } else {
      onPromptItemsChange([
        ...promptItems,
        { content: prompt, description: '', fileUrl: '', supportedDocumentUrl: '' },
      ]);
    }
    setPickerOpen(false);
    setEditingIndex(null);
    setManagePromptIndex(index);
    setExpandedPromptIndex(index);
  };

  const openRecordFlow = (index: number) => {
    setRecordModalIndex(index);
    setRecordModalOpen(true);
    setManagePromptIndex(null);
  };

  const closeRecordFlow = () => {
    setRecordModalOpen(false);
    setRecordModalIndex(null);
    setModalVideoFiles({});
  };

  const hasVideoAt = (index: number) =>
    Boolean(
      isPersistedMediaUrl(videoUrls[index]) ||
        isPersistedMediaUrl(promptItems[index]?.fileUrl) ||
        videoFiles[index]
    );

  const hasDescriptionAt = (index: number) => Boolean(promptItems[index]?.description?.trim());

  const hasDocumentAt = (index: number) =>
    Boolean(
      isPersistedMediaUrl(documentUrls[index]) ||
        isPersistedMediaUrl(promptItems[index]?.supportedDocumentUrl) ||
        documentFiles[index]
    );

  const isVideoUploadActive = (index: number) => {
    const upload = videoUploads[index];
    return Boolean(upload && upload.phase !== 'done');
  };

  const isDocumentUploadActive = (index: number) => {
    const upload = documentUploads[index];
    return Boolean(upload && upload.phase !== 'done');
  };

  const openDescriptionModal = (index: number) => {
    setDescriptionIndex(index);
    setCurrentDescription(promptItems[index]?.description || '');
    setDescriptionModalOpen(true);
    setManagePromptIndex(null);
  };

  const saveDescription = async () => {
    if (descriptionIndex == null) return;
    onPromptChange(descriptionIndex, { description: currentDescription.trim() });
    setDescriptionModalOpen(false);
    setManagePromptIndex(descriptionIndex);
    if (onPersistPromptDetails) {
      // Let React commit the description before persisting.
      window.setTimeout(() => {
        void onPersistPromptDetails();
      }, 0);
    }
  };

  const renderVideoUploadBar = (index: number) => {
    const upload = videoUploads[index];
    if (!upload || upload.phase === 'done') {
      return null;
    }

    return (
      <div className="create-prompt-video-upload create-prompt-video-upload--manage">
        <PromptVideoUploadBar progress={upload.percent} phase={upload.phase} compact />
        <div className="create-prompt-video-upload-actions">
          {upload.phase === 'error' ? (
            <button
              type="button"
              className="create-prompt-video-upload-retry"
              onClick={() => onRetryVideoUpload(index)}
            >
              Retry upload
            </button>
          ) : null}
          {upload.phase !== 'error' ? (
            <button
              type="button"
              className="create-prompt-video-upload-cancel"
              onClick={() => onCancelVideoUpload(index)}
            >
              Cancel upload
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderDocumentUploadBar = (index: number) => {
    const upload = documentUploads[index];
    if (!upload || upload.phase === 'done') {
      return null;
    }

    return (
      <div className="create-prompt-video-upload create-prompt-video-upload--manage">
        <PromptVideoUploadBar
          progress={upload.percent}
          phase={upload.phase}
          compact
          kind="document"
        />
        <div className="create-prompt-video-upload-actions">
          {upload.phase === 'error' ? (
            <button
              type="button"
              className="create-prompt-video-upload-retry"
              onClick={() => onRetryDocumentUpload(index)}
            >
              Retry upload
            </button>
          ) : null}
          {upload.phase !== 'error' ? (
            <button
              type="button"
              className="create-prompt-video-upload-cancel"
              onClick={() => onCancelDocumentUpload(index)}
            >
              Cancel upload
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const isSidebar = variant === 'sidebar';

  const renderManageModal = (index: number) => {
    const item = promptItems[index];
    if (!item) return null;

    return (
      <PromptManageModal
        show
        promptText={item.content}
        descriptionPreview={item.description?.trim()}
        hasVideo={hasVideoAt(index)}
        hasDescription={hasDescriptionAt(index)}
        hasDocument={hasDocumentAt(index)}
        uploadProgressSlot={isVideoUploadActive(index) ? renderVideoUploadBar(index) : null}
        documentUploadProgressSlot={
          isDocumentUploadActive(index) ? renderDocumentUploadBar(index) : null
        }
        onClose={() => setManagePromptIndex(null)}
        onRecordOrUpload={() => openRecordFlow(index)}
        onDeleteVideo={() => {
          onVideoFile(index, null);
          onPromptChange(index, { fileUrl: '' });
          toast.success('Video removed.');
        }}
        onViewVideo={() => {
          const remote = videoUrls[index] || item.fileUrl || '';
          if (remote && !remote.startsWith('blob:')) {
            const url = mediaUrl(remote);
            if (url) window.open(url, '_blank');
            return;
          }
          const file = videoFiles[index];
          if (file) {
            window.open(URL.createObjectURL(file), '_blank');
          }
        }}
        onDescription={() => openDescriptionModal(index)}
        onUploadDocument={() => document.getElementById(`onboarding-doc-upload-${index}`)?.click()}
        onViewDocument={() => {
          const file = documentFiles[index];
          if (file) {
            window.open(URL.createObjectURL(file), '_blank');
            return;
          }
          const url = mediaUrl(documentUrls[index] || item.supportedDocumentUrl || '');
          if (url) window.open(url, '_blank');
        }}
        onDeleteDocument={() => {
          onDocumentFile(index, null);
          onPromptChange(index, { supportedDocumentUrl: '' });
          toast.success('Supporting document removed.');
        }}
        onChangePrompt={() => {
          setManagePromptIndex(null);
          openPicker(index);
        }}
        doneLabel={
          isVideoUploadActive(index) || isDocumentUploadActive(index)
            ? 'Close & continue in background'
            : 'Done'
        }
        onDone={() => {
          setManagePromptIndex(null);
          if (isVideoUploadActive(index) || isDocumentUploadActive(index)) {
            toast.info('Upload will continue in the background.');
          }
        }}
      />
    );
  };

  return (
    <div
      className={`ssr-card ssr-video-card ${emphasized || isSidebar ? 'ssr-video-card--emphasized' : ''} ${isSidebar ? 'ssr-video-card--sidebar' : ''}`}
    >
      <div className="ssr-video-head">
        <div>
          <h3 className="ssr-heading">
            {isSidebar ? 'Suggested screening prompts' : 'Screening prompts'}
            {isSidebar || emphasized ? (
              <span className="ssr-prompt-priority-badge">High impact</span>
            ) : (
              <span className="ssr-optional-label"> (Optional)</span>
            )}
          </h3>
          {!isSidebar ? (
            <p>
              {emphasized
                ? 'Screening prompts are the most important part of your profile. Record video answers here to stand out to recruiters.'
                : 'Recording these answers can help you get noticed by recruiters.'}
            </p>
          ) : null}
        </div>
      </div>

      <div className="ssr-prompt-list">
        {promptItems.map((item, index) => (
          <div key={`prompt-wrap-${item.id ?? index}-${item.content.slice(0, 24)}`}>
            <OnboardingPromptCard
              index={index}
              item={{
                ...item,
                fileUrl: isPersistedMediaUrl(videoUrls[index] || item.fileUrl)
                  ? String(videoUrls[index] || item.fileUrl)
                  : '',
                supportedDocumentUrl: isPersistedMediaUrl(
                  documentUrls[index] || item.supportedDocumentUrl
                )
                  ? String(documentUrls[index] || item.supportedDocumentUrl)
                  : '',
              }}
              isExpanded={expandedPromptIndex === index}
              videoFile={
                isPersistedMediaUrl(videoUrls[index] || item.fileUrl)
                  ? undefined
                  : videoFiles[index]
              }
              hasDescription={hasDescriptionAt(index)}
              hasDocument={hasDocumentAt(index)}
              compact={isSidebar}
              onToggle={togglePrompt}
              onEdit={() => setManagePromptIndex(index)}
            />
            {isVideoUploadActive(index) ? (
              <div className="ssr-prompt-upload-bar">{renderVideoUploadBar(index)}</div>
            ) : null}
            {isDocumentUploadActive(index) ? (
              <div className="ssr-prompt-upload-bar">{renderDocumentUploadBar(index)}</div>
            ) : null}
          </div>
        ))}
      </div>

      {isSidebar ? (
        <>
          {promptItems.length < MAX_PROMPTS ? (
            <button
              type="button"
              className="ssr-add-prompt-btn ssr-add-prompt-btn--sidebar"
              onClick={handleAddPrompt}
            >
              <Plus size={16} aria-hidden />
              Add screening prompt
            </button>
          ) : null}
        </>
      ) : null}

      {!isSidebar && promptItems.length < MAX_PROMPTS ? (
        <button type="button" className="ssr-add-prompt-btn" onClick={handleAddPrompt}>
          <Plus size={16} aria-hidden />
          Add another screening prompt
        </button>
      ) : null}

      {pickerOpen ? (
        <Modal
          show={pickerOpen}
          onHide={() => {
            setPickerOpen(false);
            setEditingIndex(null);
          }}
          centered
          size="lg"
          dialogClassName="prompt-picker-modal"
          scrollable
        >
          <PromptSelectionModal
            jobIndustry={roleMeta.jobIndustry}
            jobTitle={roleMeta.jobTitle}
            level={roleMeta.level}
            roleSpecificPrompts={roleSpecificPrompts}
            rolePromptPoolSize={roleSpecificPool.length}
            rolePromptsLoading={rolePromptsLoading}
            rolePromptsRefreshing={rolePromptsRefreshing}
            canGenerateRolePrompts={Boolean(roleMeta.jobTitle?.trim())}
            onRefreshRolePrompts={handleRefreshRolePrompts}
            onModalClose={() => {
              setPickerOpen(false);
              setEditingIndex(null);
            }}
            onPromptSelect={handlePromptSelect}
            editingIndex={editingIndex}
            setEditingIndex={setEditingIndex}
            promptArray={promptArray}
            onClearVideoAtIndex={(index) => {
              onVideoFile(index, null);
              onPromptChange(index, { fileUrl: '' });
            }}
          />
        </Modal>
      ) : null}

      {managePromptIndex != null ? renderManageModal(managePromptIndex) : null}

      {recordModalOpen && recordModalIndex != null ? (
        <Modal
          show={recordModalOpen}
          onHide={closeRecordFlow}
          centered
          className="recording-studio-modal"
          backdropClassName="recording-studio-modal-backdrop"
          size="lg"
        >
          <RecordOrUploadModal
            editingIndex={recordModalIndex}
            recordOrUploadModal={recordModalOpen}
            onModalClose={closeRecordFlow}
            errors={recordErrors}
            setErrors={setRecordErrors}
            setVideoFiles={setModalVideoFiles}
            videoFiles={modalVideoFiles}
            openPromptIndex={recordModalIndex}
            hideSubmit
            promptText={promptItems[recordModalIndex]?.content || ''}
            preferRecordingStudio
            uploadProgressSlot={
              isVideoUploadActive(recordModalIndex) ? renderVideoUploadBar(recordModalIndex) : null
            }
            onVideoCaptured={(file) => {
              onVideoFile(recordModalIndex, file);
              closeRecordFlow();
              setManagePromptIndex(recordModalIndex);
              setExpandedPromptIndex(recordModalIndex);
            }}
            onClearVideoAtIndex={() => {
              onVideoFile(recordModalIndex, null);
              onPromptChange(recordModalIndex, { fileUrl: '' });
            }}
          />
        </Modal>
      ) : null}

      {descriptionModalOpen ? (
        <Modal
          show={descriptionModalOpen}
          onHide={() => {
            setDescriptionModalOpen(false);
            if (descriptionIndex != null) {
              setManagePromptIndex(descriptionIndex);
            }
          }}
          size="lg"
          centered
        >
          <PromptDescriptionModal
            onModalClose={() => {
              setDescriptionModalOpen(false);
              if (descriptionIndex != null) {
                setManagePromptIndex(descriptionIndex);
              }
            }}
            currentDescription={currentDescription}
            setCurrentDescription={setCurrentDescription}
            editingIndex={descriptionIndex}
            onSubmit={() => void saveDescription()}
          />
        </Modal>
      ) : null}

      {promptItems.map((_, index) => (
        <input
          key={`doc-input-${index}`}
          id={`onboarding-doc-upload-${index}`}
          type="file"
          className="prompt-doc-upload-input"
          accept=".pdf,.doc,.docx,.txt"
          aria-label={`Upload supporting document for prompt ${index + 1}`}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              onDocumentFile(index, file);
              toast.success('Document uploading…');
              setManagePromptIndex(index);
            }
            event.target.value = '';
          }}
        />
      ))}
    </div>
  );
}
